import { useState, useEffect, useCallback } from 'react';
import { hrms } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import Card from '../../components/common/Card';
import CheckInOutWidget from '../../components/common/CheckInOutWidget';
import { theme } from '../../theme';

const STATUS_COLOR = {
  present: theme.success,
  absent: theme.danger,
  half_day: theme.warning,
  late: theme.info,
  on_leave: '#8B5CF6',
  wfh: '#0EA5E9',
  holiday: '#EC4899',
  week_off: '#0891b2',
  weekend: theme.textMuted,
};

const STATUS_LABEL = {
  present: 'P',
  absent: 'A',
  half_day: 'H',
  late: 'L',
  on_leave: 'OL',
  wfh: 'WFH',
  holiday: 'HOL',
  week_off: 'WO',
};

function MyAttendance() {
  const { user } = useAuth();
  const isFlexible = user?.attendance_type === 'flexible_week_off';
  const [records, setRecords] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [tooltip, setTooltip] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [attRes, holRes] = await Promise.allSettled([
        hrms.getMyAttendance({ month, year }),
        hrms.getHolidays(year),
      ]);
      if (attRes.status === 'fulfilled') {
        const d = attRes.value.data;
        setRecords(Array.isArray(d) ? d : d.items || []);
      }
      if (holRes.status === 'fulfilled') setHolidays(holRes.value.data || []);
    } catch { setRecords([]); } finally { setLoading(false); }
  }, [month, year]);

  useEffect(() => { load(); }, [load]);

  // Build holiday lookup: "YYYY-MM-DD" → holiday object
  const holidayMap = {};
  holidays.forEach(h => { holidayMap[h.date] = h; });

  const months = ['January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'];
  const years = Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i);

  const daysInMonth = new Date(year, month, 0).getDate();
  const firstDayOfWeek = new Date(year, month - 1, 1).getDay();

  // Compute week-off days for flexible_week_off employees
  const weekOffDays = (() => {
    if (!isFlexible) return new Set();
    const recMap = {};
    records.forEach(r => { recMap[r.date?.slice(0, 10)] = r; });
    const result = new Set();
    const allDays = Array.from({ length: daysInMonth }, (_, i) => i + 1);
    const weekGroups = {};
    allDays.forEach(d => {
      const date = new Date(year, month - 1, d);
      const dow = date.getDay();
      const mondayOffset = (dow + 6) % 7;
      const monday = new Date(date);
      monday.setDate(date.getDate() - mondayOffset);
      const key = monday.toISOString().slice(0, 10);
      if (!weekGroups[key]) weekGroups[key] = [];
      weekGroups[key].push(d);
    });
    Object.values(weekGroups).forEach(weekDays => {
      const missing = weekDays.filter(d => {
        const dateStr = `${year}-${String(month).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
        const rec = recMap[dateStr];
        if (!rec) return true;
        return rec.status === 'absent';
      });
      if (missing.length >= 1) result.add(missing[0]);
    });
    return result;
  })();

  const summary = {
    present: records.filter((r) => r.status === 'present').length,
    wfh: records.filter((r) => r.status === 'wfh').length,
    absent: records.filter((r) => {
      const d = new Date(r.date);
      const dow = d.getDay();
      if (!isFlexible && (dow === 0 || dow === 6)) return false; // ignore weekend records
      return r.status === 'absent' && !weekOffDays.has(d.getDate());
    }).length,
    half_day: records.filter((r) => r.status === 'half_day').length,
    late: records.filter((r) => r.status === 'late').length,
    on_leave: records.filter((r) => r.status === 'on_leave').length,
    ...(isFlexible ? { week_off: weekOffDays.size } : {}),
  };

  const fmtTime = (t) => {
    if (!t) return null;
    const d = new Date(`1970-01-01T${t}`);
    if (isNaN(d)) return t;
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
      <CheckInOutWidget />

      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
        <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: theme.text, margin: 0 }}>My Attendance</h1>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))}
            style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
            {months.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))}
            style={{ padding: '0.5rem 0.875rem', border: `1.5px solid ${theme.border}`, borderRadius: '0.5rem', fontSize: '0.875rem', color: theme.text, outline: 'none' }}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </div>
      </div>

      {/* Summary chips */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: '0.75rem' }}>
        {Object.entries(summary).map(([key, val]) => (
          <div key={key} style={{ background: theme.white, borderRadius: '0.75rem', padding: '0.875rem 1rem', border: `1px solid ${theme.border}`, borderTop: `3px solid ${STATUS_COLOR[key] || theme.primary}`, textAlign: 'center' }}>
            <p style={{ fontSize: '1.4rem', fontWeight: 700, color: theme.text, margin: 0 }}>{val}</p>
            <p style={{ fontSize: '0.72rem', color: theme.textMuted, margin: '0.2rem 0 0', textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</p>
          </div>
        ))}
      </div>

      {/* Calendar */}
      <Card title={`${months[month - 1]} ${year}`}>
        {loading ? (
          <p style={{ textAlign: 'center', color: theme.textMuted, padding: '2rem' }}>Loading...</p>
        ) : (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px' }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d) => (
                <div key={d} style={{ textAlign: 'center', padding: '0.4rem', fontSize: '0.72rem', fontWeight: 700, color: theme.textMuted }}>{d}</div>
              ))}
              {Array.from({ length: firstDayOfWeek }, (_, i) => <div key={`e-${i}`} />)}
              {Array.from({ length: daysInMonth }, (_, i) => {
                const day = i + 1;
                const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                const rec = records.find((r) => r.date === dateStr || r.date?.startsWith(dateStr));
                const holiday = holidayMap[dateStr];
                const dow = new Date(year, month - 1, day).getDay();
                const isWeekend = dow === 0 || dow === 6;
                // General employees: Sat & Sun are always week off — ignore any records
                const isFixedWeekend = !isFlexible && isWeekend;
                const isWO = weekOffDays.has(day);
                const color = holiday ? STATUS_COLOR.holiday
                  : isFixedWeekend ? STATUS_COLOR.weekend
                  : isWO ? STATUS_COLOR.week_off
                  : rec ? STATUS_COLOR[rec.status] : null;
                const label = holiday ? 'HOL'
                  : isFixedWeekend ? '–'
                  : isWO ? 'WO'
                  : rec ? (STATUS_LABEL[rec.status] || rec.status?.substring(0, 3).toUpperCase()) : null;
                const showTooltip = !isFixedWeekend && (rec || holiday);

                return (
                  <div key={day}
                    onMouseEnter={() => showTooltip && setTooltip({ day, rec, holiday })}
                    onMouseLeave={() => setTooltip(null)}
                    style={{ position: 'relative', padding: '0.4rem', textAlign: 'center', borderRadius: '0.5rem', background: color ? `${color}18` : theme.pageBg, border: `1px solid ${color ? `${color}50` : theme.border}`, minHeight: '54px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: showTooltip ? 'pointer' : 'default' }}>
                    <span style={{ fontSize: '0.8rem', fontWeight: 600, color: (isFixedWeekend && !holiday) ? theme.textMuted : theme.text }}>{day}</span>
                    {label && <span style={{ fontSize: '0.58rem', fontWeight: 700, color: (isFixedWeekend && !holiday) ? theme.textMuted : color, marginTop: '2px', letterSpacing: '0.3px' }}>{label}</span>}
                    {!isFixedWeekend && rec?.status === 'wfh' && <span style={{ fontSize: '0.55rem', color: STATUS_COLOR.wfh }}>🏠</span>}

                    {/* Tooltip */}
                    {tooltip?.day === day && (
                      <div style={{ position: 'absolute', bottom: '110%', left: '50%', transform: 'translateX(-50%)', background: '#1E293B', color: '#fff', borderRadius: '0.5rem', padding: '0.5rem 0.75rem', fontSize: '0.72rem', whiteSpace: 'nowrap', zIndex: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.2)' }}>
                        {tooltip.holiday ? (
                          <>
                            <div style={{ fontWeight: 700, marginBottom: '3px' }}>{tooltip.holiday.name}</div>
                            <div style={{ color: '#EC4899', textTransform: 'capitalize' }}>{tooltip.holiday.holiday_type} Holiday</div>
                          </>
                        ) : rec ? (
                          <>
                            <div style={{ fontWeight: 700, textTransform: 'capitalize', marginBottom: '3px' }}>{rec.status?.replace(/_/g, ' ')}</div>
                            {fmtTime(rec.check_in) && <div>In: {fmtTime(rec.check_in)}</div>}
                            {fmtTime(rec.check_out) && <div>Out: {fmtTime(rec.check_out)}</div>}
                            {rec.hours_worked && <div>{rec.hours_worked.toFixed(1)}h worked</div>}
                            {rec.notes && <div style={{ maxWidth: '160px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{rec.notes}</div>}
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '1rem', paddingTop: '1rem', borderTop: `1px solid ${theme.border}` }}>
              {Object.entries(STATUS_COLOR).filter(([k]) => k !== 'weekend').map(([key, color]) => (
                <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                  <div style={{ width: '10px', height: '10px', borderRadius: '2px', background: color }} />
                  <span style={{ fontSize: '0.72rem', color: theme.textMuted, textTransform: 'capitalize' }}>{key.replace(/_/g, ' ')}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

export default MyAttendance;
